import { useMemo } from "react";
import { Button } from "@arco-design/web-react";

import { contactMergeStatusLabels } from "../config";
import { useContactMergeAudit } from "../runtime/useContactMergeAudit";
import { useContactMergeRuntime } from "../runtime/useContactMergeRuntime";
import {
  contactMergeActionLabel,
  toContactMergeEditorPayload,
} from "../service/contactMergeService";
import { ContactMergeImageInput } from "./ContactMergeImageInput";
import { ContactMergeEditor } from "./ContactMergeEditor";
import "./CrmContactMergePage.css";
import "../../../shared/ui/crmListPageLayout.css";

/** 重复联系人合并完整工作台。 */
export function CrmContactMergeFeaturePage() {
  const runtime = useContactMergeRuntime();
  const audit = useContactMergeAudit(
    runtime.selectedTaskId,
    Boolean(runtime.detail?.allowedActions.includes("view_audit")),
  );
  const editorCandidates = useMemo(() => {
    const all = [
      ...(runtime.detail ? [runtime.detail.target, ...runtime.detail.sources] : []),
      ...runtime.candidates,
    ];
    return Array.from(new Map(all.map((item) => [item.contactId, item])).values());
  }, [runtime.candidates, runtime.detail]);
  const editingExisting = Boolean(
    runtime.detail && runtime.detail.allowedActions.includes("edit"),
  );

  const handleAction = async (action: string) => {
    if (action === "view_audit" || action === "edit") return;
    if (
      action === "execute_direct" &&
      !window.confirm("确认一键执行联系人合并？系统将自动提交审核、审核通过并立即修改正式新旧库，删除重复联系人。")
    ) {
      return;
    }
    if ((action === "execute" || action === "retry") && !window.confirm("确认执行联系人合并？该操作会删除旧库重复联系人。")) {
      return;
    }
    const needsReason = ["reject", "resolve_exception", "cancel"].includes(action);
    const reason = needsReason ? window.prompt("请输入处理原因：") ?? undefined : undefined;
    if (needsReason && !reason) return;
    await runtime.handleAction(action, reason);
  };

  return (
    <main className="crm-list-page contact-merge-page">
      {runtime.errorMessage ? <div role="alert">{runtime.errorMessage}</div> : null}
      {!runtime.metadata?.executeEnabled ? (
        <div className="contact-merge-notice">
          当前执行开关关闭：可创建、识别、预览和审批，不能正式执行合并。
        </div>
      ) : null}

      <section className="crm-list-panel contact-merge-list-card">
        <div className="crm-list-section-header">
          <h2>合并任务列表</h2>
          <div className="crm-list-table-actions">
            <Button
              type="primary"
              onClick={() => {
                runtime.setSelectedTaskId(undefined);
                runtime.setEditorOpen(true);
              }}
            >
              新建合并任务
            </Button>
          </div>
        </div>
        <div className="crm-list-panel-body">
        <div className="contact-merge-filter-row">
          <input
            aria-label="任务关键词"
            placeholder="任务号或旧库联系人 ID"
            value={runtime.keyword}
            onChange={(event) => runtime.setKeyword(event.target.value)}
          />
          <select
            aria-label="任务状态"
            value={runtime.status ?? ""}
            onChange={(event) => runtime.setStatus(event.target.value || undefined)}
          >
            <option value="">全部状态</option>
            {Object.entries(contactMergeStatusLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className="contact-merge-table-wrap">
          <table>
            <thead>
              <tr>
                <th>任务号</th>
                <th>保留联系人</th>
                <th>待删除数</th>
                <th>状态</th>
                <th>检查点</th>
                <th>更新时间</th>
              </tr>
            </thead>
            <tbody>
              {runtime.tasks.map((task) => (
                <tr
                  key={task.id}
                  className={runtime.selectedTaskId === task.id ? "is-selected" : ""}
                  onClick={() => runtime.setSelectedTaskId(task.id)}
                >
                  <td><button type="button">{task.taskNo}</button></td>
                  <td>{task.targetContactName}</td>
                  <td>{task.sourceCount}</td>
                  <td>{contactMergeStatusLabels[task.status] ?? task.status}</td>
                  <td>{task.currentStage}</td>
                  <td>{task.updatedAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="contact-merge-pagination">
          <button
            type="button"
            disabled={runtime.page <= 1}
            onClick={() => runtime.handlePageChange(runtime.page - 1)}
          >
            上一页
          </button>
          <span>第 {runtime.page} 页，共 {runtime.total} 条</span>
          <button
            type="button"
            disabled={runtime.page * runtime.pageSize >= runtime.total}
            onClick={() => runtime.handlePageChange(runtime.page + 1)}
          >
            下一页
          </button>
        </div>
        </div>
      </section>

      {runtime.editorOpen ? (
        <section className="crm-list-panel contact-merge-editor-card">
          <div className="crm-list-section-header">
            <h2>{editingExisting ? "编辑合并任务" : "新建合并任务"}</h2>
          </div>
          <div className="crm-list-panel-body">
          <div className="contact-merge-candidate-toolbar">
            <label className="contact-merge-candidate-field">
              <span>学校</span>
              <input
                aria-label="候选学校"
                placeholder="请输入学校名称"
                value={runtime.candidateSchoolName}
                onChange={(event) =>
                  runtime.setCandidateSchoolName(event.target.value)
                }
              />
            </label>
            <label className="contact-merge-candidate-field">
              <span>联系人姓名</span>
              <input
                aria-label="候选联系人姓名"
                placeholder="输入姓名或姓名片段"
                value={runtime.candidateContactName}
                onChange={(event) =>
                  runtime.setCandidateContactName(event.target.value)
                }
              />
            </label>
            <label className="contact-merge-candidate-field">
              <span>电话</span>
              <input
                aria-label="候选电话"
                placeholder="输入主电话或备用电话"
                value={runtime.candidatePhone}
                onChange={(event) =>
                  runtime.setCandidatePhone(event.target.value)
                }
              />
            </label>
            <button
              type="button"
              disabled={
                runtime.candidateLoading ||
                !(
                  runtime.candidateSchoolName.trim() ||
                  runtime.candidateContactName.trim() ||
                  runtime.candidatePhone.trim()
                )
              }
              onClick={runtime.handleSearchCandidates}
            >
              {runtime.candidateLoading ? "搜索中..." : "搜索候选"}
            </button>
          </div>
          <ContactMergeImageInput
            recognition={runtime.recognition}
            recognitionPending={runtime.recognitionPending}
            recognitionErrorMessage={runtime.recognitionErrorMessage}
            onRecognize={runtime.handleRecognizeImage}
            onClear={runtime.clearRecognition}
          />
          {runtime.phoneFallbackApplied ? (
            <p role="status">已按电话补充重复联系人。</p>
          ) : null}
          {editorCandidates.length > 0 ? (
            <ContactMergeEditor
              key={runtime.detail?.version ?? "create"}
              candidates={editorCandidates}
              errorMessage={runtime.saveErrorMessage}
              initialValue={runtime.detail ? toContactMergeEditorPayload(runtime.detail) : undefined}
              submitting={runtime.mutating}
              onSubmit={(payload) => {
                if (editingExisting) void runtime.handleUpdate(payload);
                else void runtime.handleCreate(payload);
              }}
            />
          ) : runtime.candidateSearchSubmitted ? (
            <p role="status">
              未找到符合条件的联系人，请确认当前环境已同步该联系人数据，或调整学校、姓名或电话后重试。
            </p>
          ) : (
            <p>请先搜索候选联系人，或粘贴截图自动提取搜索线索。</p>
          )}
          </div>
        </section>
      ) : null}

      {runtime.detail && !runtime.editorOpen ? (
        <section className="crm-list-panel contact-merge-detail-card">
          <div className="crm-list-section-header contact-merge-detail-header">
            <div>
              <h2>{runtime.detail.taskNo}</h2>
              <p>
                {runtime.detail.target.contactName} ← 合并 {runtime.detail.sources.length} 个联系人
              </p>
            </div>
            <span>{contactMergeStatusLabels[runtime.detail.status] ?? runtime.detail.status}</span>
          </div>
          <div className="crm-list-panel-body">
          <div className="contact-merge-actions">
            {runtime.detail.allowedActions.includes("edit") ? (
              <button type="button" onClick={() => runtime.setEditorOpen(true)}>编辑</button>
            ) : null}
            {runtime.detail.allowedActions
              .filter((action) => !["edit", "view_audit"].includes(action))
              .map((action) => (
                <button
                  type="button"
                  key={action}
                  disabled={
                    runtime.mutating ||
                    (["execute", "execute_direct"].includes(action) && !runtime.metadata?.executeEnabled)
                  }
                  onClick={() => void handleAction(action)}
                >
                  {contactMergeActionLabel(action)}
                </button>
              ))}
          </div>
          <div className="contact-merge-snapshot-grid">
            {[runtime.detail.target, ...runtime.detail.sources].map((contact) => (
              <article key={contact.contactId}>
                <h4>{contact.contactName}</h4>
                <p>{contact.legacyId}</p>
                <dl>
                  <dt>Phone</dt><dd>{contact.phone || "（空）"}</dd>
                  <dt>Phone2</dt><dd>{contact.phone2 || "（空）"}</dd>
                  <dt>Remark</dt><dd>{contact.remark || "（空）"}</dd>
                  <dt>CollegeDepatmentMajorID</dt><dd>{contact.collegeDepartmentMajorId || "（空）"}</dd>
                  <dt>DepartMajorName</dt><dd>{contact.departMajorName || "（空）"}</dd>
                  <dt>WeiXinCode</dt><dd>{contact.weixinCode || "（空）"}</dd>
                </dl>
              </article>
            ))}
          </div>
          {runtime.detail.impactSummary ? (
            <details open>
              <summary>两库影响预览</summary>
              <pre>{JSON.stringify(runtime.detail.impactSummary, null, 2)}</pre>
            </details>
          ) : null}
          {runtime.detail.errorSummary ? (
            <div role="alert">{runtime.detail.errorCode}: {runtime.detail.errorSummary}</div>
          ) : null}
          {runtime.detail.allowedActions.includes("view_audit") ? (
            <details>
              <summary>审计时间线</summary>
              {audit.isLoading ? <p>加载中...</p> : null}
              <ol>
                {(audit.data ?? []).map((event) => (
                  <li key={event.id}>
                    <strong>{event.eventType}</strong> · {event.stage || "-"} · 操作人 {event.operatorUserId} · {event.createdAt}
                  </li>
                ))}
              </ol>
            </details>
          ) : null}
          </div>
        </section>
      ) : null}
    </main>
  );
}
